import { useFormikContext } from 'formik';
import { ReactNode } from 'react';
import { GitCommitIcon, LayersIcon, LayoutGridIcon } from 'lucide-react';

import { addPlural } from '@/react/common/string-utils';

import { StickyFooter } from '@@/StickyFooter/StickyFooter';
import { Button, LoadingButton } from '@@/buttons';
import { Icon } from '@@/Icon';

import { FormValues } from './type';
import { useWizardContext } from './WizardContext';

export function WizardFooter() {
  const { isFirstStep, isLastStep, goToPreviousStep, currentStepIndex } =
    useWizardContext();
  const { isSubmitting, isValid, values } = useFormikContext<FormValues>();

  const targetGroupCount = new Set(
    values.artifacts.flatMap((artifact) => artifact.targets)
  ).size;

  return (
    <StickyFooter>
      <div className="flex gap-4">
        {currentStepIndex > 0 && values.sources.length > 0 && (
          <Stat label="Sources" icon={GitCommitIcon}>
            {addPlural(values.sources.length, 'source')}
          </Stat>
        )}
        {currentStepIndex > 1 && values.artifacts.length > 0 && (
          <Stat label="Artifacts" icon={LayersIcon}>
            {addPlural(values.artifacts.length, 'artifact')}
          </Stat>
        )}
        {currentStepIndex > 2 && values.artifacts.length > 0 && (
          <Stat label="Targets" icon={LayoutGridIcon}>
            {addPlural(targetGroupCount, 'group')}
          </Stat>
        )}
      </div>

      <div className="ml-auto flex justify-end gap-4">
        <Button
          color="default"
          type="button"
          onClick={goToPreviousStep}
          disabled={isFirstStep}
          data-cy="gitops-source-wizard-back-button"
          size="medium"
        >
          Back
        </Button>
        <LoadingButton
          color="primary"
          type="submit"
          disabled={!isValid}
          data-cy="gitops-source-wizard-continue-button"
          size="medium"
          isLoading={isSubmitting}
          loadingText={isLastStep ? 'Creating...' : ''}
        >
          {isLastStep ? 'Create' : 'Continue'}
        </LoadingButton>
      </div>
    </StickyFooter>
  );
}

function Stat({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-6">
        <Icon icon={icon} /> {label}
      </div>
      <div className="font-semibold tracking-wider text-gray-10">
        {children}
      </div>
    </div>
  );
}
